"use client";

import * as React from "react";
import TextComponent from "../common/Textcomponent";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  ChevronDown,
  Search,
  FileDown,
  FileX2,
  FileText,
  Loader,
} from "lucide-react";
import { toast } from "@components/ui/use-toast";
import { Button } from "@components/ui/button";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { useEffect } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { DataTableProps, DataTableFilterProps } from "./CommonTypes";
import { useDebounce } from "@hooks/useDebounce";
import { GeneralContext } from "@services/providers/GeneralProvider";
import { Checkbox } from "../ui/checkbox";
import CustomTooltip from "./CustomTooltip";

import { jsPDF } from "jspdf";
import "jspdf-autotable";
import InfoTooltip from "./InfoTooltip";
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// declare global {
//   interface Window {
//     jspdf: any; // Declare the global jspdf object
//   }
// }
const DataTable: React.FC<DataTableProps> = ({
  cols = [],
  data,
  sort = true,
  colFilter = true,
  search = true,
  pagination = true,
  padding = true,
  border = true,
  showHeader = true,
  searchText = "",
  visibleColumns = {},
  extraOptions,
  noSortColumns = [],
  className = "",
  totalCount,
  rowsPerPage = 10,
  countPerPageSelection = false,
  apiSearch = false,
  filterFunction,
  loading = false,
  onRowClick,
  rowClickColumn,
  rowsSelection,
  rowsSelectionType = "id",
  _getSelectedRows,
  allowXScroll = false,
  customPagination = false,
  showDownload = false,
  initialPageIndex,
  isHighlightedRow = false,
  onDownloadCSV,
  rightOptions,
  reportHeaderFooter,
  stickyPagination = false,
  isHighlightBg = false,
  showPagination = true,
  highlightedRowId,
  nextPreviousCursor,
  isSearchVisibleCols = false,
}) => {
  const { theme } = React.useContext(GeneralContext);
  const headerRef = React.useRef(null);
  const [pageLoading, setPageLoading] = React.useState(false);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  // const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [filter, setFilter] = React.useState<DataTableFilterProps>({
    searchText: searchText,
    sortKey: "",
    sortOrder: "",
    pageIndex: initialPageIndex || 0,
    pageSize: rowsPerPage,
  });
  const [headerHtml, setHeaderHtml] = React.useState<string>("");
  // const [footerHtml, setFooterHtml] = React.useState<string>("");

  const debouncedSearch = useDebounce((term: any) => {
    if (filterFunction) {
      filterFunction(term, table.getRowModel().rows);
    }
  }, 500);

  useEffect(() => {
    debouncedSearch(filter);
  }, [filter]);

  useEffect(() => {
    setFilter({
      ...filter,
      pageIndex: initialPageIndex || 0,
    });
  }, [initialPageIndex]);

  useEffect(() => {
    setPageLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (apiSearch) {
      if (filter.searchText != searchText) {
        filter.searchText = searchText;
        setFilter({ ...filter });
      }
    } else {
      // setGlobalFilter(searchText);
      filterRows(searchText);
      filter.pageIndex = 0;
      setFilter({ ...filter });
    }
  }, [searchText]);

  useEffect(() => {
    setFilteredData(data);
    if (!apiSearch) {
      filterRows(tableFilter);
      const totalCount = (initialPageIndex || 0) * filter.pageSize;
      if (data.length <= totalCount && (initialPageIndex || 0) > 0) {
        setFilter({
          ...filter,
          pageIndex: 0,
        });
      }
    }
  }, [data]);

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(
      Object.keys(visibleColumns).length > 0 ? visibleColumns : {},
    );
  const [rowSelection, setRowSelection] = React.useState({});
  const [filteredData, setFilteredData] = React.useState(data);
  const [tableFilter, setTableFilter] = React.useState(searchText);
  // const [selectAllRows,setSelectAllRows] = React.useState(false)

  // useEffect(()=>{
  //   table.toggleAllPageRowsSelected(true)
  // },[selectAllRows])

  if (cols.length == 0) {
    for (const key in data[0]) {
      cols.push({ header: key, accessor: key });
    }
  }

  function changeSortKey(key: string, sortOrderProp?: string) {
    let order = sortOrderProp ? sortOrderProp : "ASC";
    if (filter.sortKey == key) {
      order = filter.sortOrder == "ASC" ? "DESC" : "ASC";
    }
    filter.sortKey = key;
    filter.sortOrder = order;
    setFilter({ ...filter });
  }

  function localTextSearch(txt: string) {
    filter.searchText = txt;
    filter.pageIndex = 0;
    setFilter({ ...filter });
    filterRows(txt);
  }

  const columns: any[] = [];

  const anchorTagRegex = /<a[^>]*>([^<]*)</;

  const downloadFilterCSV = async () => {
    if (onDownloadCSV && typeof onDownloadCSV === "function") {
      const filterIds = table
        .getFilteredRowModel()
        .rows.map((r: any) => r.original.globalId);
      onDownloadCSV("csv", filterIds);
      return;
    }
    try {
      // setLoading(true);

      setTimeout(async () => {
        const filteredData = await Promise.all(
          table.getFilteredRowModel().rows.map(async (row) =>
            Promise.all(
              row
                .getVisibleCells()
                .filter((cell) => cell.column.id !== "action")
                .map(async (cell) => {
                  let val = extractText(cell.getValue() as any);
                  const match = val?.match(anchorTagRegex);
                  if (match && match[1]) {
                    val = match[1];
                  }
                  val = val?.replaceAll(",", ""); // Remove commas
                  return val.replace(/<\/?[^>]+(>|$)/g, "");
                }),
            ),
          ),
        );

        const header = Array.from(
          new Set(
            table
              .getVisibleFlatColumns()
              .filter((col) => {
                const header = col?.columnDef?.header;
                if (typeof header === "function") {
                  return typeof header("col" as any)?.props?.children ==
                    "string"
                    ? header("col" as any)?.props?.children
                    : header("col" as any)?.props?.children?.[0];
                }
                return header;
              })
              .map((col) => {
                const header = col?.columnDef?.header;
                if (typeof header === "function") {
                  return typeof header("col" as any)?.props?.children ==
                    "string"
                    ? header("col" as any)?.props?.children
                    : header("col" as any)?.props?.children?.[0];
                }
                return header;
              }),
          ),
        );
        const csvRows = filteredData.map((row) => row).join("\n");

        const csv = [...header.values()].join(",") + "\n" + csvRows;

        const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "AML_Report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // setLoading(false);
        toast({
          title: "Success!!",
          description: "Filtered logs downloaded successfully",
          variant: "success",
        });
      }, 0);
    } catch (error) {
      // setLoading(false);
      toast({
        title: "Error",
        description: "An error occurred while downloading the file.",
        variant: "destructive",
      });
      console.error("Download Error:", error);
    }
  };

  const downloadFilterExcel = async () => {
    if (onDownloadCSV && typeof onDownloadCSV === "function") {
      const filterIds = table
        .getFilteredRowModel()
        .rows.map((r: any) => r.original.globalId);
      onDownloadCSV("excel", filterIds);
      return;
    }
    try {
      // setLoading(true);

      setTimeout(async () => {
        const filteredData = await Promise.all(
          table.getFilteredRowModel().rows.map(async (row) =>
            Promise.all(
              row
                .getVisibleCells()
                .filter((cell) => cell.column.id !== "action")
                .map(async (cell) => {
                  let val = extractText(cell.getValue() as any);
                  const match = val?.match(anchorTagRegex);
                  if (match && match[1]) {
                    val = match[1];
                  }
                  val = val?.replaceAll(",", ""); // Remove commas
                  return val.replace(/<\/?[^>]+(>|$)/g, "");
                }),
            ),
          ),
        );

        const header = Array.from(
          new Set(
            table
              .getVisibleFlatColumns()
              .filter((col) => {
                const header = col?.columnDef?.header;
                if (typeof header === "function") {
                  return typeof header("col" as any)?.props?.children ==
                    "string"
                    ? header("col" as any)?.props?.children
                    : header("col" as any)?.props?.children?.[0];
                }
                return header;
              })
              .map((col) => {
                const header = col?.columnDef?.header;
                if (typeof header === "function") {
                  return typeof header("col" as any)?.props?.children ==
                    "string"
                    ? header("col" as any)?.props?.children
                    : header("col" as any)?.props?.children?.[0];
                }
                return header;
              }),
          ),
        );

        // console.log(header)
        const csvRows = filteredData.map((row) => row.join("\t")).join("\n");

        const csv = [...header.values()].join("\t") + "\n" + csvRows;

        const csvBlob = new Blob([csv], { type: "application/vnd.ms-excel;" });
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "AML_Report.xls");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // setLoading(false);
        toast({
          title: "Success!!",
          description: "Filtered logs downloaded successfully",
          variant: "success",
        });
      }, 0);
    } catch (error) {
      // setLoading(false);
      toast({
        title: "Error",
        description: "An error occurred while downloading the file.",
        variant: "destructive",
      });
      console.error("Download Error:", error);
    }
  };

  useEffect(() => {
    if (reportHeaderFooter) {
      if (reportHeaderFooter.header) {
        // console.log(reportHeaderFooter.header);
        let header = reportHeaderFooter.header;
        const parser = new DOMParser();
        const doc = parser.parseFromString(header, "text/html");
        const imgElements = doc.querySelectorAll("img");
        imgElements.forEach((img) => {
          const src = img.getAttribute("src");
          if (
            src &&
            src.startsWith(
              "/opt/linkshadow/dashboard/leo/htmlreportcode/all/images/ls-logo.png",
            )
          ) {
            img.setAttribute(
              "src",
              src.replace(
                "/opt/linkshadow/dashboard/leo/htmlreportcode/all/images/ls-logo.png",
                "/static/ls/images/ls-logo-white.png",
              ),
            );
          } else if (src && src.startsWith("/opt/linkshadow/dashboard/leo/")) {
            img.setAttribute(
              "src",
              src.replace("/opt/linkshadow/dashboard/leo/", "/"),
            );
          }
          img.style.height = "33px";
        });

        const allTables = doc.querySelectorAll("table");
        if (allTables.length > 0) {
          const outermostTable = allTables[0];
          const newRow = doc.createElement("tr");
          newRow.innerHTML = `
             <td colspan="100%">
               <h2 style="font-size:16px;font-weight:bold;color:#ae2027;margin:0;text-align:center;">
                 AML Report
               </h2>
             </td>
           `;

          outermostTable.appendChild(newRow);
          outermostTable.style.width = "620px";
        }

        const p = doc.querySelectorAll("p");
        p.forEach((paragraph) => {
          paragraph.style.marginTop = "10px";
          paragraph.style.marginBottom = "10px";
        });

        doc.querySelectorAll("td[valign]").forEach((td) => {
          const existingStyle = td.getAttribute("style") || "";
          td.setAttribute("style", `${existingStyle}; font-size:10px;`);
        });

        header = doc.documentElement.outerHTML;
        setHeaderHtml(header);
      } else {
        setHeaderHtml("");
      }

      // if (headerFooter.footer) {
      //   let footer = headerFooter.footer;
      //   const parser = new DOMParser();
      //   const doc = parser.parseFromString(footer, 'text/html');
      //   const table = doc.querySelector('table');
      //   if (table) {
      //     table.style.width = "620px"; // Set width to 100%
      //   }
      //   const a = doc.querySelectorAll('a');
      //   if(a) {
      //     a.forEach((a) => {
      //      const existingStyle = a.getAttribute('style') || '';
      //      a.setAttribute('style', `${existingStyle}; color:#be202e!important; font-size:10px;`);
      //     });
      //     footer = doc.documentElement.outerHTML;
      //   }
      //   setFooterHtml(footer);
      // }else{
      //   setFooterHtml("");
      // }
    } else {
      setHeaderHtml("");
      // setFooterHtml("");
    }
  }, [reportHeaderFooter]);

  const downloadFilterPDF = async () => {
    if (onDownloadCSV && typeof onDownloadCSV === "function") {
      const filterIds = table
        .getFilteredRowModel()
        .rows.map((r: any) => r.original.globalId);
      onDownloadCSV("pdf", filterIds);
      return;
    }
    setPageLoading(true);
    try {
      // Wait for all the data to be processed (filtered data)
      const filteredData = await Promise.all(
        table.getFilteredRowModel().rows.map(async (row) =>
          Promise.all(
            row
              .getVisibleCells()
              .filter((cell) => cell.column.id !== "action")
              .map(async (cell) => {
                let val = extractTextForPDF(cell.getValue() as any); // Extract the cell value
                const match = val?.match(anchorTagRegex); // Check if there's a match for anchor tags
                if (match && match[1]) {
                  val = match[1]; // Replace value with match if available
                }
                val = val?.replaceAll(",", ""); // Remove commas from the value
                return val.replace(/<\/?[^>]+(>|$)/g, ""); // Return cleaned value
              }),
          ),
        ),
      );

      const header1 = Array.from(
        new Set(
          table
            .getVisibleFlatColumns()
            .filter((col) => {
              const header = col?.columnDef?.header;
              if (typeof header === "function") {
                return typeof header("col" as any)?.props?.children == "string"
                  ? header("col" as any)?.props?.children
                  : header("col" as any)?.props?.children?.[0];
              }
              return header;
            })
            .map((col) => {
              const header = col?.columnDef?.header;
              if (typeof header === "function") {
                return typeof header("col" as any)?.props?.children == "string"
                  ? header("col" as any)?.props?.children
                  : header("col" as any)?.props?.children?.[0];
              }
              return header;
            }),
        ),
      );
      // autoTable(jsPDF); // Apply the plugin
      const pdf = new jsPDF("landscape", "px", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const columns = header1;
      const rows = filteredData;

      type ColumnStyles = {
        [key: number]: { cellWidth: number }; // Numeric index signature
      };

      const columnStyles: ColumnStyles = {};
      header1.forEach((colname, index) => {
        if (colname == "Events List") {
          columnStyles[index] = { cellWidth: 120 }; // Apply desired width to each column
        } else if (colname == "Description") {
          columnStyles[index] = { cellWidth: 100 };
        }
      });

      // ==============================================================================================================

      const headerElement: any = headerRef?.current; //document.getElementById("pdf-header");
      if (headerElement) {
        headerElement.style.display = "block";
        await pdf.html(headerElement, {
          x: 10,
          y: 10,
          width: pdfWidth,
          html2canvas: {
            scale: 1,
            useCORS: true,
            backgroundColor: null,
          },
        });
        headerElement.style.display = "none";
      }

      pdf.autoTable({
        head: [columns], // Header row
        body: rows, // Data rows
        startY: 100, // Starting Y position
        theme: "striped", // 'striped', 'grid', or 'plain'
        styles: {
          fontSize: 8, // Font size
          cellPadding: 5, // Cell padding
          overflow: "linebreak",
        },
        columnStyles: columnStyles,
        headStyles: {
          fillColor: [171, 36, 48], // Background color for header
          textColor: [255, 255, 255], // Text color for header
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240], // Alternating row background color
        },
      });

      const footerElement = document.getElementById("pdf-footer");
      if (footerElement) {
        footerElement.style.display = "block";
        const totalPages = pdf.getNumberOfPages();
        pdf.setPage(totalPages);
        const pageHeight = pdf.internal.pageSize.getHeight();
        await pdf.html(footerElement, {
          x: 10,
          y: pageHeight - 80,
          width: pdfWidth,
          html2canvas: {
            scale: 1,
            useCORS: true,
            backgroundColor: null,
          },
        });
        footerElement.style.display = "none";
      }

      pdf.save("AML_Report.pdf");

      // ==============================================================================================================

      // pdf.autoTable({
      //   head: [columns], // Header row
      //   body: rows, // Data rows
      //   startY: 20, // Starting Y position
      //   theme: "striped", // 'striped', 'grid', or 'plain'
      //   styles: {
      //     fontSize: 8, // Font size
      //     cellPadding: 5, // Cell padding
      //     overflow: "linebreak",
      //   },
      //   columnStyles: columnStyles,
      //   headStyles: {
      //     fillColor: [171, 36, 48], // Background color for header
      //     textColor: [255, 255, 255], // Text color for header
      //     fontStyle: "bold",
      //   },
      //   alternateRowStyles: {
      //     fillColor: [240, 240, 240], // Alternating row background color
      //   },
      // });
      // pdf.save("AML_Report.pdf");

      // Show a success toast
      toast({
        title: "Success!!",
        description: "Filtered logs downloaded successfully as PDF",
        variant: "success",
      });
    } catch (error) {
      // Show an error toast if something goes wrong
      toast({
        title: "Error",
        description: "An error occurred while downloading the PDF.",
        variant: "destructive",
      });
      console.error("Download Error:", error); // Log the error to the console
    }
    setPageLoading(false);
  };

  for (const col of cols) {
    let coltype = "";
    if ("type" in col) {
      coltype = col.type;
    }
    columns.push({
      type: coltype,
      accessorKey: col.accessor,
      header: ({ column }: { column: any }) => {
        if (sort == false || noSortColumns.includes(col.accessor)) {
          return (
            <div className="font-bold text-primary-foreground text-xs">
              {col.header}
              {col.info && <InfoTooltip text={col.info} customClass="ms-1" />}
            </div>
          );
        } else if (apiSearch) {
          return (
            <Button
              variant="link"
              className="px-0 font-bold text-primary-foreground text-xs"
              onClick={() => changeSortKey(col.accessor, col.sortOrders)}
            >
              {col.header}
              {col.info && <InfoTooltip text={col.info} customClass="ms-1" />}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        }
        return (
          <Button
            variant="link"
            className="px-0 font-bold text-primary-foreground text-xs"
            onClick={() => {
              column.toggleSorting(column.getIsSorted() === "asc");
              changeSortKey(col.accessor);
            }}
          >
            {col.header}
            {col.info && <InfoTooltip text={col.info} customClass="ms-1" />}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }: { row: any }) => {
        return <div className="font-medium">{row.getValue(col.accessor)}</div>;
      },
      sortingFn: "sortingFn" in col ? col.sortingFn : "alphanumeric",
    });
  }

  if (rowsSelection) {
    const rowsSelectionColumn = {
      type: "component",
      id: "_select_row_",
      header: ({ table }: { table: any }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
            // table.getIsAllPageRowsSelected() ||
            // (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(value);
          }}
          aria-label="Select all"
        />
      ),
    };
    columns.unshift(rowsSelectionColumn);
  }

  useEffect(() => {
    return () => {
      if (rowsSelection != undefined && rowsSelection == false) {
        // table.toggleAllRowsSelected(false);
        setRowSelection({});
      }
    };
  }, [rowsSelection]);

  useEffect(() => {
    // rowsSelection && _getSelectedRows && _getSelectedRows(table.getSelectedRowModel().rows)// Will return row
    if (rowsSelection && _getSelectedRows) {
      if (rowsSelectionType == "id") {
        _getSelectedRows(Object.keys(table.getState().rowSelection)); // Will return row id
      } else if (rowsSelectionType == "row") {
        _getSelectedRows(
          table.getSelectedRowModel().rows.map((row: any) => row.original),
        ); // Will return row
      }
    }
  }, [rowSelection]);

  // Props that can carry visible text, collected by extractText
  const TEXT_PROPS = [
    "children",
    "htmlContent",
    "title",
    "alt",
    "label",
    "value",
  ];

  const extractText = (node: any): string => {
    if (node == null || typeof node === "boolean") {
      return "";
    } else if (typeof node === "string" || typeof node === "number") {
      return node.toString() + " ";
    } else if (Array.isArray(node)) {
      return node.map((child) => extractText(child)).join(""); // Recursively call for child nodes
    } else if (typeof node !== "object") {
      return "";
    } else if (node.props) {
      // React element: collect every text bearing prop, not just the first hit
      const html = node.props.dangerouslySetInnerHTML?.__html;
      return (
        (html ? html + " " : "") +
        TEXT_PROPS.map((prop) => extractText(node.props[prop])).join("")
      );
    } else {
      // Plain data object, eg. the raw row values passed by filterRows
      return Object.values(node)
        .map((value) => extractText(value))
        .join("");
    }
  };

  const extractTextForPDF = (node: any): string => {
    // alert(node.type)
    if (typeof node === "string" || typeof node === "number") {
      return node.toString() + " ";
    } else if (Array.isArray(node)) {
      if (typeof node[-1] == "string") {
        node[node.length - 1] = node.slice(-1) + "\n";
        node[0] = ">>" + node[0];
      }
      const finaltext = node.map(extractTextForPDF);
      return finaltext.join("") + "\n"; // Recursively call for child nodes
    } else if (node && typeof node === "object") {
      let bulletText = "";
      let bulletText1 = "";
      if (node.type == "li") {
        bulletText = "• ";
        bulletText1 = "";
      }
      if (node.type == "ul") {
        // bulletText = "\n"
      }
      return (
        bulletText +
        extractTextForPDF(
          node.props && (node.props.children || node.props.htmlContent),
        ) +
        bulletText1
      ); // Check for children prop
    } else {
      return "";
    }
  };

  const tableState: any = {
    sorting,
    columnFilters,
    columnVisibility,
    rowSelection,
  };

  if (!apiSearch) {
    tableState["pagination"] = filter;
  }

  const table = useReactTable({
    data: filteredData.length == 0 && tableFilter == "" ? data : filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: false,
    state: tableState,
    initialState: {
      pagination: {
        pageIndex: filter.pageIndex || 0,
        pageSize: filter.pageSize,
      },
    },
    getRowId: (row, index) => row.id || index,
  });

  function filterRows(e: string) {
    setTableFilter(e);
    const temp: any = [];
    data.forEach((row: any) => {
      const allKeys = isSearchVisibleCols
        ? cols.map((col: any) => col.accessor)
        : Object.keys(row);
      for (const key of allKeys) {
        if (
          extractText(row[key])
            .replace(/\s+/g, "")
            .toLowerCase()
            .includes(
              e.replace(/\s+/g, "").replace("LSGUID_", "").toLowerCase(),
            )
        ) {
          temp.push(row);
          break;
        }
      }
    });
    setFilteredData(temp);
  }

  React.useEffect(() => {
    if (filterFunction && !apiSearch) {
      filterFunction(filter, table.getRowModel().rows);
    }
  }, [filteredData]);

  function pageIndexClick(index: number) {
    if (apiSearch) {
      setPageLoading(true);
    }
    filter.pageIndex = index;
    setFilter({ ...filter });
    table.setPageIndex(index);
  }

  function pageSizeChanged(size: number) {
    filter.pageSize = size;
    filter.pageIndex = 0;
    setFilter({ ...filter });
    table.setPageSize(size);
    table.setPageIndex(0);
  }

  function changeSearch(e: string) {
    filter.searchText = e;
    filter.pageIndex = 0;
    setFilter({ ...filter });
  }

  const pages = Math.ceil(
    (apiSearch ? (totalCount ?? table.getRowCount()) : filteredData.length) /
      filter.pageSize,
  );

  const [rowId, setRowId] = React.useState<string | number | null>(
    isHighlightedRow ? 0 : null,
  );

  React.useEffect(() => {
    const rows = table.getRowModel().rows;
    if (rowId === 0 && rows.length > 0) {
      const temp = Array.isArray(rows[0]?.id) ? rows[0]?.id : [rows[0]?.id];
      setRowId(Number(temp));
    }
  }, [table.getRowModel().rows.length]);

  React.useEffect(() => {
    if (highlightedRowId !== undefined && isHighlightedRow) {
      setRowId(highlightedRowId);
    }
  }, [highlightedRowId, isHighlightedRow]);

  const allRowCount = apiSearch ? totalCount || 0 : filteredData.length || 0;
  const tableBodyHeightClass = search
    ? pagination
      ? "dt-tbody-search-pagination"
      : "dt-tbody-search-only"
    : pagination
      ? "dt-tbody-pagination-only"
      : "dt-tbody-default";

  return (
    <div
      className={
        stickyPagination
          ? `flex flex-col h-[calc(100%)] ${className}`
          : `w-full overflow-y-auto ${className}`
      }
    >
      <div
        className={`flex items-center relative border-b -mb-1 z-level-1 shadow pb-4 ${
          colFilter || search ? "" : "hidden"
        }`}
      >
        <Search className="h-4 w-4 text-foreground absolute ms-3" />
        {apiSearch ? (
          <input
            placeholder="Search..."
            value={filter.searchText}
            onChange={(value) => changeSearch(value.target.value)}
            className={`focus:border-primary border focus:outline-none bg-background text-sm p-2 ps-10 rounded-md w-96 ${search ? "" : "hidden"}`}
          />
        ) : (
          <input
            placeholder="Search..."
            value={tableFilter ?? ""}
            onChange={(value) => localTextSearch(value.target.value)}
            className={`focus:border-primary border focus:outline-none bg-background text-sm p-2 ps-10 rounded-md w-96 ${search ? "" : "hidden"}`}
          />
        )}

        {/* {rowsSelection && (
          <Button 
          variant="outline"
          className="ms-2"
          onClick={()=>{
            // setSelectAllRows(true)
            // table.getRowModel().rows.forEach(row => {
            //   console.log(row)
            // });
            // table.getFilteredRowModel().rows.map(row => {
            //   console.log(row)
            // })
            // console.log(data)
            table.toggleAllPageRowsSelected(true)
          }}
          >Select All</Button>
        )} */}

        {extraOptions ? <div>{extraOptions}</div> : ""}
        <div className={`flex-1 flex`}>
          <div className="flex justify-end gap-2 px-4 text-right grow items-center">
            {rightOptions ? <div>{rightOptions}</div> : ""}
            {showDownload && (
              // <div className="inline-block items-center w-28">
              <div className="flex items-center pr-2 gap-2">
                <CustomTooltip text="Download Logs CSV">
                  <FileText
                    size={20}
                    className="cursor-pointer hover:text-primary"
                    onClick={downloadFilterCSV}
                  />
                </CustomTooltip>
                <CustomTooltip text="Download Logs Excel">
                  <FileX2
                    size={20}
                    className="cursor-pointer hover:text-primary"
                    onClick={downloadFilterExcel}
                  />
                </CustomTooltip>
                <CustomTooltip text="Download Logs PDF">
                  <FileDown
                    size={20}
                    className="cursor-pointer hover:text-primary"
                    onClick={downloadFilterPDF}
                  />
                </CustomTooltip>
              </div>
            )}
            {countPerPageSelection && (
              <div>
                <span className="text-sm">Show</span>
                <div className="inline-block m-1">
                  <Select
                    value={filter.pageSize.toString()}
                    onValueChange={(e: string) => pageSizeChanged(parseInt(e))}
                  >
                    <SelectTrigger className="w-24 ms-2">
                      <SelectValue placeholder="10" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm ps-2">Entries</span>
              </div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={"ml-auto " + (colFilter ? "" : "hidden")}
              >
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  let name = column.id;
                  for (const col of cols) {
                    if (col.accessor == column.id) {
                      name = col.header;
                    }
                  }
                  if (name != "") {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {name}
                      </DropdownMenuCheckboxItem>
                    );
                  }
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className={stickyPagination ? "flex-1 overflow-y-auto" : ""}>
        <div
          className={`min-h-40 rounded-md w-full ${allowXScroll ? "[&>div]:overflow-x-auto" : "[&>div]:overflow-x-hidden"} relative ${border ? "border" : ""}`}
        >
          {pageLoading && (
            <div>
              <div className="bg-[#00000030] z-modal-level-1 w-full h-full glass-2 rounded-md absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 flex flex-col justify-center items-center gap-2">
                <Loader className="animate-spin !w-10 !h-10" />
                <div className="text-xs animate-pulse">Loading</div>
              </div>
            </div>
          )}
          <Table
            className={
              "w-full " +
              (allowXScroll
                ? " overflow-x-auto whitespace-nowrap"
                : " overflow-x-hidden whitespace-normal")
            }
          >
            {showHeader ? (
              <TableHeader>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: any, index: number) => (
                    <TableRow key={index}>
                      {headerGroup.headers.map((header: any, index: number) => {
                        return (
                          <TableHead
                            key={index}
                            className={`${padding ? "" : "first:ps-0 last:pe-0"} text-xs`}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
              </TableHeader>
            ) : (
              ""
            )}
            <TableBody
              className={`dt-tbody-height-control ${tableBodyHeightClass}`}
            >
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row: any, index: number) => (
                  <TableRow
                    id={row.id}
                    key={index}
                    className={
                      (onRowClick ? "cursor-pointer" : "") +
                      (rowId == row.id
                        ? isHighlightBg
                          ? " [&>td]:!bg-primary [&>td>div]:text-highlight"
                          : " !border-2 !border-primary shadow-lg rounded-md"
                        : "")
                    }
                    onClick={() => {
                      if (isHighlightedRow) setRowId(row.id);
                      if (onRowClick) {
                        onRowClick(
                          rowClickColumn
                            ? row.original[rowClickColumn]
                            : row.original,
                        );
                      }
                    }}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell: any, ind: number) => {
                      const colid = cell.column.id;
                      if (rowsSelection && colid == "_select_row_") {
                        return (
                          <TableCell
                            key={"0"}
                            className={`${padding ? "" : "first:ps-0 last:pe-0"} text-xs`}
                          >
                            <Checkbox
                              id={colid + ind.toString}
                              checked={row.getIsSelected()}
                              onCheckedChange={(value) => {
                                row.toggleSelected(!!value);
                              }}
                              aria-label="Select row"
                            />
                          </TableCell>
                        );
                      }
                      let coltype = "";
                      for (const col of cols) {
                        if (col.accessor == colid) {
                          if ("type" in col) {
                            coltype = col.type;
                          }
                        }
                      }
                      if (coltype == "") {
                        const renderedContent: any = flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        );

                        return (
                          <TableCell
                            key={ind}
                            className={`${padding ? "" : "first:ps-0 last:pe-0"} text-xs`}
                          >
                            <TextComponent
                              htmlContent={renderedContent?.props?.getValue?.()}
                            />
                          </TableCell>
                        );
                      } else if (coltype == "component") {
                        const renderedContent: any = flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        );
                        const randomindex = Math.random()
                          .toString(36)
                          .substring(5);
                        return (
                          <TableCell
                            key={randomindex + "_" + index + "_" + cell.id}
                            className={`${padding ? "" : "first:ps-0 last:pe-0"} text-xs`}
                          >
                            {renderedContent?.props?.getValue?.()}
                          </TableCell>
                        );
                      }
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 px-3 border-y-2"
                  >
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <div
        className={
          stickyPagination
            ? `dt-pagination-row sticky bottom-0 bg-primary-background border-t flex items-center justify-end space-x-2 py-4 shadow ${
                !pageLoading && pagination ? "" : "hidden"
              }`
            : `dt-pagination-row flex items-center border-t justify-end space-x-2 py-4 ${
                !pageLoading && pagination ? "" : "hidden"
              }`
        }
      >
        {!customPagination && showPagination && (totalCount ?? 0) > 0 && (
          <div className="flex-1 text-sm text-muted-foreground">
            Showing {filter.pageIndex * filter.pageSize + 1} to{" "}
            {(filter.pageIndex + 1) * filter.pageSize > allRowCount
              ? allRowCount
              : (filter.pageIndex + 1) * filter.pageSize}{" "}
            of{" "}
            {totalCount &&
            totalCount > 0 &&
            filteredData.length > 0 &&
            filteredData.length != totalCount &&
            !apiSearch
              ? filteredData.length
              : (totalCount ?? table.getRowCount())}{" "}
            {totalCount && totalCount > 1 ? "rows" : "row"}
            {totalCount &&
              totalCount > 0 &&
              filteredData.length > 0 &&
              filteredData.length != totalCount &&
              !apiSearch && (
                <span>
                  {" "}
                  (Filtered {filteredData.length} of {totalCount}{" "}
                  {totalCount > 1 ? "rows" : "row"})
                </span>
              )}
          </div>
        )}
        <div className="dt-pagination-controls space-x-2">
          {showPagination && (totalCount ?? 0 > 0) ? (
            <div className="inline-block me-2 align-middle">
              {filter.pageIndex > 3 && pages > 5 ? (
                <div className="inline-block">
                  <Button
                    key={0}
                    size="sm"
                    variant="outline"
                    onClick={() => pageIndexClick(0)}
                    className="me-2 text-xs h-8"
                  >
                    1
                  </Button>
                  <div className="inline-block">...</div>
                </div>
              ) : (
                ""
              )}
              {Array.from({ length: pages }).map((_, index) => {
                if (
                  Math.abs(filter.pageIndex - index) <= 2 ||
                  (index <= 4 && filter.pageIndex <= 3) ||
                  (index >= pages - 5 && filter.pageIndex >= pages - 3)
                ) {
                  return (
                    <Button
                      key={index}
                      size="sm"
                      variant={
                        filter.pageIndex == index ? "default" : "outline"
                      }
                      onClick={() => pageIndexClick(index)}
                      className={
                        "ms-2 text-xs shadow-lg h-8 " +
                        (filter.pageIndex == index
                          ? "text-white"
                          : theme == "dark"
                            ? "text-white"
                            : "text-black")
                      }
                    >
                      {index + 1}
                    </Button>
                  );
                }
              })}
              {filter.pageIndex + 2 < pages && pages > 5 ? (
                <div className="inline-block ms-2">
                  <div className="inline-block">...</div>
                  <Button
                    key={pages}
                    size="sm"
                    variant="outline"
                    onClick={() => pageIndexClick(pages - 1)}
                    className={
                      "ms-2 text-xs shadow-lg h-8 " +
                      (theme == "dark" ? "text-white" : "text-black")
                    }
                  >
                    {pages}
                  </Button>
                </div>
              ) : (
                ""
              )}
            </div>
          ) : (
            ""
          )}
          <Button
            variant="outline"
            size="sm"
            className="shadow-lg"
            onClick={() => {
              table.setPageIndex(filter.pageIndex - 1);
              filter.pageIndex = filter.pageIndex - 1;
              setFilter({ ...filter });
              if (apiSearch || loading) {
                setPageLoading(true);
              }
            }}
            disabled={
              filter.pageIndex == 0 ||
              (nextPreviousCursor && nextPreviousCursor?.previous == "")
            }
          >
            {!customPagination ? "Previous" : "<"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="shadow-lg"
            onClick={() => {
              table.setPageIndex(filter.pageIndex + 1);
              filter.pageIndex = filter.pageIndex + 1;
              setFilter({ ...filter });
              if (apiSearch || loading) {
                setPageLoading(true);
              }
            }}
            disabled={
              (pages && filter.pageIndex > pages - 2) ||
              (nextPreviousCursor && nextPreviousCursor?.next == "") ||
              filteredData.length == 0
            }
          >
            {!customPagination ? "Next" : ">"}
          </Button>
        </div>
      </div>

      <div className="absolute -right-full overflow-hidden w-0 h-0">
        <div
          ref={headerRef}
          className="w-[620px] text-xs"
          style={{ display: "none" }}
          dangerouslySetInnerHTML={{ __html: headerHtml }}
        />
      </div>

      {/* <div
        id="pdf-footer"
        className="w-[620px] text-xs"
        style={{ display: 'none' }}
        dangerouslySetInnerHTML={{ __html: footerHtml }}
      /> */}
      {/* Hiding the footer because it always appears on the first page. */}
    </div>
  );
};

export default DataTable;
